import React, { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import { withFormsy } from 'formsy-react'
import { DangerIcon, EyeCrossed, EyeIcon, MagnifyIcon, CrossIcon } from '../../../assets/icons'
import IconContainer from '@/components/ui/iconContainer'
import ButtonIcon from '@/components/ui/ButtonIcon'
import Button from '@/components/ui/button'

const TextField = React.forwardRef(
  (
    {
      label,
      error = '',
      value = '',
      helperText,
      onChange = () => {},
      onBlur = () => {},
      onFocus = () => {},
      className = '',
      suffix = '',
      prefix = '',
      disabled = false,
      format,
      containerClassName = '',
      maxChars,
      autofocus = false,
      icon,
      textFieldContainerSlot,
      // Trailing-action variants (InputText behavior):
      // 'none' (default) | 'icon' (ButtonIcon) | 'button' (text-only Button)
      variant = 'none',
      trailingIcon = null,
      buttonLabel = 'Search',
      showClear = false,
      onEnter = () => {},
      onClear = () => {},
      ...inputProps
    },
    ref
  ) => {
    const textField = useRef()
    const [suffixPosition, setSuffixPosition] = useState(0)
    const [isFocused, setIsFocused] = useState(false)
    const [passwordVisible, setPasswordVisible] = useState(false)
    const textWidthRef = useRef(null)
    const handleFocus = () => {
      setIsFocused(true)
      onFocus()
    }

    const handleBlur = e => {
      setIsFocused(false)
      onBlur(e.target.value)
    }

    const handleKeyDown = e => {
      if (e.key === 'Enter') onEnter(value)
      if (inputProps.onKeyDown) inputProps.onKeyDown(e)
    }

    const handleClear = () => {
      onChange('')
      onClear()
    }

    const handleTrailingAction = () => onEnter(value)

    const renderTrailingVariant = () => {
      if (variant === 'icon') {
        return (
          <>
            {/* vertical divider, neutral-3, 4px away from the icon via the row's gap-1 */}
            <span className="w-px self-stretch my-1 bg-Neutral-3 shrink-0" aria-hidden />
            <ButtonIcon
              variant="normal"
              size="small"
              cursorPointer
              highlightOnHover
              isDisabled={disabled}
              handleOnClick={handleTrailingAction}
              ariaLabel={buttonLabel}>
              {trailingIcon || <MagnifyIcon />}
            </ButtonIcon>
          </>
        )
      }
      if (variant === 'button') {
        return (
          <Button onClick={handleTrailingAction} disabled={disabled} size="medium" type="text-only">
            {buttonLabel}
          </Button>
        )
      }
      return null
    }
    useEffect(() => {
      if (autofocus && textField.current) {
        textField?.current.focus()
      }
    }, [autofocus, textField])

    useEffect(() => {
      if (suffix) {
        updateSuffixPosition()
      }
    }, [value])

    const updateSuffixPosition = () => {
      if (textWidthRef.current) {
        setSuffixPosition(textWidthRef.current.offsetWidth + 5)
      }
    }

    // Determine styles based on state
    const getContainerStyle = () => {
      if (error) {
        return {
          borderColor: 'var(--color-negative-4)',
          color: 'var(--color-negative-4)',
        }
      }
      if (disabled) {
        return {
          borderColor: 'var(--color-Neutral-3)',
          color: 'var(--color-Neutral-4)',
          backgroundColor: 'var(--color-Neutral-3)',
          opacity: 0.5,
          cursor: 'default',
          pointerEvents: 'none',
        }
      }
      if (isFocused) {
        return {
          borderColor: 'var(--color-brand-04)',
          color: 'var(--color-Neutral-6)',
        }
      }
      return {
        borderColor: 'var(--color-Neutral-3)',
        color: 'var(--color-Neutral-6)',
      }
    }

    const getInputStyle = () => {
      if (error) {
        return { color: 'var(--color-negative-4)' }
      }
      if (disabled) {
        return { color: 'var(--color-Neutral-4)' }
      }
      return { color: 'var(--color-Neutral-6)' }
    }

    const getLabelStyle = () => {
      if (error) {
        return { color: 'var(--color-negative-4)' }
      }
      return { color: 'var(--color-Neutral-6)' }
    }

    const getSuffixStyle = () => {
      return {
        left: suffixPosition,
        color: 'var(--color-Neutral-6)',
      }
    }

    const containerClasses = classnames(
      'relative flex flex-col transition-colors duration-200 px-4 py-4 h-14 rounded-lg border border-solid bg-surface-2 w-full',
      {
        'px-4 py-0': !label,
        'min-h-[56px]': !inputProps.isTextArea,
        'h-auto': inputProps.isTextArea,
        // Border highlights to Neutral-6 on hover only while at rest (inline borderColor needs `!`).
        'hover:!border-[var(--color-Neutral-6)]': !error && !disabled && !isFocused,
      },
      containerClassName
    )

    const textFieldClassNames = classnames(
      className,
      'w-full bg-transparent outline-none border-none text-base placeholder:text-neutral-400',
      {
        'h-full': !inputProps.isTextArea,
        'resize-none min-h-[100px]': inputProps.isTextArea,
        'no-spinner': inputProps.type === 'number',
      }
    )
    const randomId = Math.random()
    const generalProps = {
      ...inputProps,
      id: inputProps.id || randomId,
      ref: ref || textField,
      onChange: e => {
        const value = e.target.value
        if (!maxChars || (maxChars && value.length <= maxChars && onChange))
          return onChange(format ? format(value) : value)
      },
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      value: format ? format(value) : value,
      className: textFieldClassNames,
      onFocus: handleFocus,
      type: inputProps.type === 'password' ? (passwordVisible ? 'text' : 'password') : inputProps.type,
      placeholder: inputProps.placeholder,
      disabled,
      readOnly: inputProps.readOnly,
      style: getInputStyle(),
    }
    return (
      <div className="w-full space-y-2">
        {label && (
          <label
            className="block font-bold text-sm transition-colors"
            style={getLabelStyle()}
            htmlFor={inputProps.id || randomId}>
            {label}
          </label>
        )}
        <div className={containerClasses} style={getContainerStyle()}>
          <div className="relative w-full h-full flex flex-col justify-center">
            <div className="flex items-center gap-1 w-full h-full">
              {prefix && <span className="text-neutral-400 mr-2">{prefix}</span>}
              <div className="relative w-full h-full">
                {!inputProps.isTextArea ? <input {...generalProps} /> : <textarea {...generalProps} />}
              </div>
              <span ref={textWidthRef} style={{ visibility: 'hidden', position: 'absolute', whiteSpace: 'pre' }}>
                {value || inputProps.placeholder}
              </span>
              {suffix && (
                <span
                  style={getSuffixStyle()}
                  className="absolute text-base font-normal pl-4 pointer-events-none"
                >
                  {suffix}
                </span>
              )}
              {showClear && value && !disabled && (
                <button
                  type="button"
                  className="flex items-center justify-center shrink-0 mr-2 cursor-pointer border-none bg-transparent text-Neutral-5 hover:text-Neutral-6"
                  onClick={handleClear}
                  aria-label="Clear">
                  <CrossIcon width="12px" height="12px" />
                </button>
              )}
              {variant !== 'none' && (
                <div className="shrink-0 flex items-center gap-1 self-stretch">
                  {renderTrailingVariant()}
                </div>
              )}
            </div>
            <div className="absolute right-[3px] top-[3px] flex items-center gap-2">
              {inputProps.type == 'password' && (
                <div onClick={() => setPasswordVisible(v => !v)} className="cursor-pointer">
                  {passwordVisible ? (
                    <IconContainer size={19}>
                      <EyeIcon width="19px" outline />
                    </IconContainer>
                  ) : (
                    <IconContainer size={19}>
                      <EyeCrossed width="18px" />
                    </IconContainer>
                  )}
                </div>
              )}
              {icon && <IconContainer size={19}>{icon}</IconContainer>}
            </div>
            {textFieldContainerSlot}
          </div>
        </div>

        {error && (
          <div className="flex gap-1 items-center text-sm" style={{ color: 'var(--color-negative-4)' }}>
            <DangerIcon width="14px" outline fill="var(--color-negative-4)" /> <div>{error}</div>
          </div>
        )}
        {helperText && <div className="text-neutral-400 text-sm">{helperText}</div>}
      </div>
    )
  }
)

const TextFieldFormsy = withFormsy(props => {
  const [touched, setTouched] = useState(false)
  const formsyBindProps = {
    ...props,
    onChange: value => {
      props.setValue(value)
      if (props.onChange) {
        props.onChange(value)
        if (!props.isValidValue(value) && props.onError) props.onError()
      }
    },
    onBlur: e => {
      if (props.onBlur) props.onBlur(e)

      setTouched(!!props.value)
      if (props.validateOnBlur) props.validateOnBlur(e)
    },
    onFocus: e => {
      if (props.onFocus) props.onFocus(e)
    },
    value: props.value || '',
    error: props.error || (props.value && touched && props.errorMessage),
  }
  return <TextField ref={props.innerRef} {...formsyBindProps} />
})

export default TextField
export { TextFieldFormsy }
